import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { alpha, CircularProgress, IconButton, Tooltip, useTheme } from '@mui/material'
import { Pause as PauseIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material'
import type { ConversationTurn, UUID } from '@/index'
import { standardTooltipProps } from '@/common/components'
import { useConversationControlsState } from '../hooks'

interface InterruptAndResumeControlProps {
  readonly conversationId: UUID
  readonly sessionId: UUID
  readonly conversationTurn: ConversationTurn | undefined
  readonly onInterrupt: () => void
  readonly onResume: () => void
}

export const InterruptAndResumeControl: FC<InterruptAndResumeControlProps> = ({
  conversationId,
  sessionId,
  conversationTurn,
  onInterrupt,
  onResume,
}) => {
  const theme = useTheme()
  const { canPause, canResume, isSubmitting } = useConversationControlsState(
    conversationId,
    sessionId,
    conversationTurn,
  )

  if (!canPause && !canResume) {
    return null
  }

  const isPaused = canResume
  const tooltip = isPaused ? 'Resume Claude Code' : 'Interrupt Claude Code'
  const onClick = isPaused ? onResume : onInterrupt
  const color = isPaused ? theme.palette.success.main : theme.palette.warning.main

  return (
    <Tooltip {...standardTooltipProps} title={tooltip}>
      <span>
        <IconButton
          onClick={onClick}
          disabled={isSubmitting}
          size="small"
          sx={controlButtonSx(color)}
        >
          {resolveControlIcon(isSubmitting, isPaused)}
        </IconButton>
      </span>
    </Tooltip>
  )
}

function resolveControlIcon(isSubmitting: boolean, isPaused: boolean) {
  if (isSubmitting) {
    return <CircularProgress size={16} sx={loadingSpinnerSx} />
  }

  if (isPaused) {
    return <PlayArrowIcon sx={controlIconSx} />
  }

  return <PauseIcon sx={controlIconSx} />
}

const controlButtonSx = (color: string): SxProps<Theme> => ({
  padding: '4px',
  color: color,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(color, 0.08),
  },
})

const controlIconSx: SxProps<Theme> = {
  fontSize: '1.3rem',
}

const loadingSpinnerSx: SxProps<Theme> = {
  color: 'inherit',
}
